/**
 * Household type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base household properties matching HouseholdBase schema
export interface HouseholdBase {
  user_id: string | null
  parent_first_name: string
  parent_last_name: string
  email: string
  phone: string | null
  secondary_phone: string | null
  address: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relationship: string | null
  doctor_first_name: string | null
  doctor_last_name: string | null
  doctor_phone: string | null
  insurance_provider: string | null
  referral_source: string | null
  agreed_to_policy: boolean | null
  agreed_to_hold_harmless: boolean | null
  policy_agreement_date: string | null
  billing_email: string | null
  auto_pay_enabled: boolean | null
  notes: string | null
  is_active: boolean | null
}

// Create household data matching HouseholdCreate schema
export interface HouseholdCreate extends HouseholdBase {}

// Update household data matching HouseholdUpdate schema
export interface HouseholdUpdate {
  user_id?: string | null
  parent_first_name?: string
  parent_last_name?: string
  email?: string
  phone?: string | null
  secondary_phone?: string | null
  address?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  emergency_contact_relationship?: string | null
  doctor_first_name?: string | null
  doctor_last_name?: string | null
  doctor_phone?: string | null
  insurance_provider?: string | null
  referral_source?: string | null
  agreed_to_policy?: boolean | null
  agreed_to_hold_harmless?: boolean | null
  policy_agreement_date?: string | null
  billing_email?: string | null
  auto_pay_enabled?: boolean | null
  notes?: string | null
  is_active?: boolean | null
}

// Full household response matching HouseholdResponse schema
export interface HouseholdPublic extends HouseholdBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface HouseholdResponse extends HouseholdPublic {
  // Enriched field populated by the backend
  user_email?: string | null
}

// Paginated list response matching HouseholdListResponse schema
export interface HouseholdListResponse {
  items: HouseholdResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: HouseholdListMeta
}

export interface HouseholdListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: HouseholdFilters
}

// Form data interfaces for UI components
export interface HouseholdFormData {
  user_id?: string | null
  parent_first_name?: string
  parent_last_name?: string
  email?: string
  phone?: string | null
  secondary_phone?: string | null
  address?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  emergency_contact_relationship?: string | null
  doctor_first_name?: string | null
  doctor_last_name?: string | null
  doctor_phone?: string | null
  insurance_provider?: string | null
  referral_source?: string | null
  agreed_to_policy?: boolean | null
  agreed_to_hold_harmless?: boolean | null
  policy_agreement_date?: string | null
  billing_email?: string | null
  auto_pay_enabled?: boolean | null
  notes?: string | null
  is_active?: boolean | null
}

export interface HouseholdUpdateFormData {
  user_id?: string | null
  parent_first_name?: string
  parent_last_name?: string
  email?: string
  phone?: string | null
  secondary_phone?: string | null
  address?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  emergency_contact_relationship?: string | null
  doctor_first_name?: string | null
  doctor_last_name?: string | null
  doctor_phone?: string | null
  insurance_provider?: string | null
  referral_source?: string | null
  agreed_to_policy?: boolean | null
  agreed_to_hold_harmless?: boolean | null
  policy_agreement_date?: string | null
  billing_email?: string | null
  auto_pay_enabled?: boolean | null
  notes?: string | null
  is_active?: boolean | null
}

// Filter interface for API queries
export interface HouseholdFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
}

// Component prop interfaces following sophisticated pattern
export interface AddHouseholdProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditHouseholdProps {
  household: HouseholdResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteHouseholdProps {
  household: HouseholdResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const householdCreateSchema = z.object({
  user_id: z.string().trim().max(32).nullable().optional(),
  parent_first_name: z.string().trim(),
  parent_last_name: z.string().trim(),
  email: z.string().trim(),
  phone: z.string().trim().nullable().optional(),
  secondary_phone: z.string().trim().nullable().optional(),
  address: z.string().trim().nullable().optional(),
  emergency_contact_name: z.string().trim().nullable().optional(),
  emergency_contact_phone: z.string().trim().nullable().optional(),
  emergency_contact_relationship: z.string().trim().nullable().optional(),
  doctor_first_name: z.string().trim().nullable().optional(),
  doctor_last_name: z.string().trim().nullable().optional(),
  doctor_phone: z.string().trim().nullable().optional(),
  insurance_provider: z.string().trim().nullable().optional(),
  referral_source: z.string().trim().nullable().optional(),
  agreed_to_policy: z.boolean().nullable().optional(),
  agreed_to_hold_harmless: z.boolean().nullable().optional(),
  policy_agreement_date: z.string().nullable().optional(),
  billing_email: z.string().trim().nullable().optional(),
  auto_pay_enabled: z.boolean().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
})

export const householdUpdateSchema = z.object({
  user_id: z.string().trim().max(32).nullable().optional(),
  parent_first_name: z.string().trim().optional(),
  parent_last_name: z.string().trim().optional(),
  email: z.string().trim().optional(),
  phone: z.string().trim().nullable().optional(),
  secondary_phone: z.string().trim().nullable().optional(),
  address: z.string().trim().nullable().optional(),
  emergency_contact_name: z.string().trim().nullable().optional(),
  emergency_contact_phone: z.string().trim().nullable().optional(),
  emergency_contact_relationship: z.string().trim().nullable().optional(),
  doctor_first_name: z.string().trim().nullable().optional(),
  doctor_last_name: z.string().trim().nullable().optional(),
  doctor_phone: z.string().trim().nullable().optional(),
  insurance_provider: z.string().trim().nullable().optional(),
  referral_source: z.string().trim().nullable().optional(),
  agreed_to_policy: z.boolean().nullable().optional(),
  agreed_to_hold_harmless: z.boolean().nullable().optional(),
  policy_agreement_date: z.string().nullable().optional(),
  billing_email: z.string().trim().nullable().optional(),
  auto_pay_enabled: z.boolean().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
})

// Type inference from Zod schemas
export type HouseholdCreateInput = z.infer<typeof householdCreateSchema>
export type HouseholdUpdateInput = z.infer<typeof householdUpdateSchema>

// Wizard-specific schema (subset of fields for onboarding)
export const householdWizardSchema = z.object({
  parent_first_name: z.string().min(1, "First name is required"),
  parent_last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Phone number is required"),
  emergency_contact_name: z
    .string()
    .min(1, "Emergency contact name is required"),
  emergency_contact_phone: z
    .string()
    .min(10, "Emergency contact phone is required"),
  emergency_contact_relationship: z.string().min(1, "Relationship is required"),
})

export type HouseholdWizardData = z.infer<typeof householdWizardSchema>
